import React, { useState, useEffect, useCallback } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useParams, useNavigate } from "react-router-dom";
import LeftPanel from '../components/AutoRegisterFormComponents/LeftPanel';
import RightPanel from '../components/AutoRegisterFormComponents/RightPanel';
import { sendVerificationEmail, validateCompanyData } from '../functions/AutoRegisterForm/newCompanyFunctions';
import { validatePhoneNumber } from "../utils/formFieldsValidator";
import { trackRegistrationStart, trackRegistrationStep, trackRegistrationComplete, trackRegistrationError, getStoredUtmParams } from "../utils/analytics";
import { startRegistrationSession, trackRegistrationEvent, trackRegistrationField, trackRegistrationSelection, isRegistrationSessionFlagged } from "../utils/registrationTracking";
import PageMeta from "../seo/PageMeta";
import { Helmet } from 'react-helmet';

function AutoRegisterForm() {
  // Feature flag - set to false to restore original behavior with all steps visible
  const HIDE_REGISTRATION_METHOD_STEP = true;
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const viewWidth = typeof window !== 'undefined' ? window.innerWidth : null;
  const MOBILE_VIEW_WIDTH_THRESHOLD = 900;
  const IS_MOBILE_VIEW = viewWidth && viewWidth <= MOBILE_VIEW_WIDTH_THRESHOLD;
  
  const { step: urlStep } = useParams();
  const navigate = useNavigate();
  
  const currentStep = urlStep ? parseInt(urlStep, 10) : 1;
  
  const getMaxStep = () => {
    return 1;
  };
  
  useEffect(() => {
    if (!urlStep || isNaN(parseInt(urlStep, 10)) || parseInt(urlStep, 10) < 1 || parseInt(urlStep, 10) > getMaxStep()) {
      navigate('/register/1', { replace: true });
    }
  }, [urlStep, navigate]);
  
  const initialFormData = {
    // Personal data (Step 1)
    name: '', // string - user's first name
    surname: '', // string - user's last name (optional)
    email: '', // string - email address
    phone: '', // string - phone number (max 12 digits + optional + at the beginning)

    // Company data (Step 1)
    companyName: '', // string - company name
    companyAddress: 'Adres firmy', // string - hardcoded placeholder sent to backend
    nip: '0000000000', // string - hardcoded placeholder sent to backend
    acceptedTerms: false, // boolean - whether the terms have been accepted
    captchaToken: '', // string - reCAPTCHA token captured on final step
    
    // Company type (Step 3)
    companyType: '', // 'teren' | 'agencja' | 'production' | 'other'
    
    // Time tracking method (Step 4)
    timeTrackingMethod: '', // 'manual' | 'gps'
    
    // GPS mode (Step 5 - only when timeTrackingMethod === 'gps')
    gpsMode: '', // 'gps_manual' | 'gps_auto' (deprecated)
    
    // Registration method (Step 5 - only when timeTrackingMethod !== 'gps')
    registrationMethod: '', // 'individual' | 'supervisor'
    
    // Schedule usage (Step 6 - only when timeTrackingMethod === 'gps')
    scheduleUsage: '' // 'yes' | 'no'
  };
  
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem('autoRegisterFormData');
    const parsed = saved ? JSON.parse(saved) : {};
    return {
      ...initialFormData,
      ...parsed,
      companyAddress: 'Adres firmy',
      nip: '0000000000'
    };
  });

  // Save formData to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('autoRegisterFormData', JSON.stringify(formData));
  }, [formData]);

  const [content, setContent] = useState(null);
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [showCaptchaModal, setShowCaptchaModal] = useState(false);
  const [showPhoneConfirmationModal, setShowPhoneConfirmationModal] = useState(false);
  const [phoneConfirmationError, setPhoneConfirmationError] = useState(null);
  const [captchaError, setCaptchaError] = useState(null);
  const RESEND_COOLDOWN_SECONDS = 60; // how many seconds to wait before resending verification email
  const SHOW_RESEND_EMAIL_SECTION = true; // toggle to show/hide "Email nie przyszedł?" and resend button
  
  const [stepValidation, setStepValidation] = useState({
    1: false
  });
  const [hasTrackedStart, setHasTrackedStart] = useState(false);
  const [lastTrackedStep, setLastTrackedStep] = useState(null);

  const formDataRef = React.useRef(formData);
  const sessionInitializedRef = React.useRef(false);
  const selectionRef = React.useRef({
    companyType: formData.companyType,
    timeTrackingMethod: formData.timeTrackingMethod,
    gpsMode: formData.gpsMode,
    registrationMethod: formData.registrationMethod,
    scheduleUsage: formData.scheduleUsage,
    acceptedTerms: formData.acceptedTerms
  });

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    if (sessionInitializedRef.current) return;
    sessionInitializedRef.current = true;

    const utmParams = getStoredUtmParams();
    startRegistrationSession({
      source: "auto_register_form",
      pagePath: window?.location?.pathname || null,
      referrer: document?.referrer || null,
      userAgent: navigator?.userAgent || null,
      ...(utmParams || {}),
    }).then(async (sessionId) => {
      trackRegistrationEvent("session_start", { source: "auto_register_form" });

      const flagged = await isRegistrationSessionFlagged(sessionId);
      if (flagged) return;

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.egodziny.pl/api';
      const APP_ID = process.env.REACT_APP_PUBLIC_ID;

      // Send mail via backend that form has been started
      fetch(`${API_BASE_URL}/mail_account_form_started`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'X-EGODZINY-APP-ID': APP_ID,
        },
        body: JSON.stringify({ session_id: sessionId })
      }).catch((error) => console.warn("Failed to notify form start", error));
    });
  }, []);

  useEffect(() => {
    fetch('/data/content.json')
      .then(response => response.json())
      .then(data => {
        setContent(data.autoRegisterForm);
      })
      .catch(error => console.error('Error loading content:', error));
  }, []);

  useEffect(() => {
    if (!SHOW_RESEND_EMAIL_SECTION) return;
    if (!success || resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [success, resendCooldown, SHOW_RESEND_EMAIL_SECTION]);

  const handleNextStep = () => {
    const maxStep = getMaxStep();
    const nextStep = Math.min(currentStep + 1, maxStep);
    navigate('/register/' + nextStep);
  };

  const normalizePhoneValue = useCallback((value) => {
    const cleanValue = value.replace(/[^0-9+\s]/g, '');
    let normalized = cleanValue;

    if (cleanValue.includes('+')) {
      const parts = cleanValue.split('+');
      const digitsAndSpaces = parts.join('').replace(/[^0-9\s]/g, '');
      normalized = `+${digitsAndSpaces}`;
    } else {
      normalized = cleanValue.replace(/[^0-9\s]/g, '');
    }

    normalized = normalized.replace(/\s{2,}/g, ' ');
    const digitsOnly = normalized.replace(/[^0-9]/g, '');
    if (digitsOnly.length > 12) return null;

    return normalized;
  }, []);

  const getVerificationRouting = useCallback((apiResult) => {
    const requiresSmsVerification = apiResult?.requires_sms_verification === true;
    const smsVerificationUrl = typeof apiResult?.sms_verification_url === "string"
      ? apiResult.sms_verification_url.trim()
      : "";
    const verificationUrl = typeof apiResult?.verification_url === "string"
      ? apiResult.verification_url.trim()
      : "";
    const fallbackToEmail = apiResult?.fallback_to_email === true;
    const hasVerificationEmailAlreadySentError = Array.isArray(apiResult?.errors)
      && apiResult.errors.includes("verification_email_already_sent");

    const shouldRedirectToSms = requiresSmsVerification
      && Boolean(smsVerificationUrl)
      && (
        apiResult?.status === 200
        || (apiResult?.status === 409 && hasVerificationEmailAlreadySentError)
      );

    return {
      shouldRedirectToSms,
      smsVerificationUrl,
      verificationUrl,
      fallbackToEmail
    };
  }, []);

  const handleNewUserCreation = async (formData) => {
    try {
      setIsLoading(true); // Start loading
      setError(null); // Clear any previous errors
      // console.log("=== Starting new user creation process ===");
      // console.log("Raw formData:", formData);
      
      // Validate the form data
      const validation = validateCompanyData(formData);
      if (!validation.isValid) {
        console.error("Validation failed:", validation.errors);
        trackRegistrationError({
          source: "auto_register_form",
          flow: formData.timeTrackingMethod || "unknown",
          step: currentStep,
          error_type: "validation_failed"
        });
        trackRegistrationEvent("submit_validation_failed", {
          source: "auto_register_form",
          flow: formData.timeTrackingMethod || "unknown",
          step: currentStep,
          errors: validation.errors || []
        });
        setError({
          title: "Błąd walidacji",
          message: "Dane formularza są nieprawidłowe",
          details: validation.errors
        });
        return;
      }
      
      trackRegistrationEvent("submit_started", {
        source: "auto_register_form",
        flow: formData.timeTrackingMethod || "unknown",
        step: currentStep
      });

      // Call the API to send verification email
      const result = await sendVerificationEmail(formData, false);
      // console.log("=== Verification email sent successfully ===");
      // console.log("API Response:", result);

      const { shouldRedirectToSms, smsVerificationUrl, verificationUrl, fallbackToEmail } = getVerificationRouting(result);
      if (shouldRedirectToSms) {
        await trackRegistrationEvent("sms_redirect", {
          source: "auto_register_form",
          flow: formData.timeTrackingMethod || "unknown",
          step: currentStep
        });
        localStorage.removeItem('autoRegisterFormData');
        window.location.assign(smsVerificationUrl);
        return;
      }
      
      // Check if API returned success: false
      if (result && result.success === false) {
        if (result.status === 409 && result.errors && result.errors.includes("verification_email_already_sent")) {
          setError({
            title: "Mail został już wysłany",
            message: <span dangerouslySetInnerHTML={{ __html: result.message || "Na podany adres email został już wysłany link weryfikacyjny." }} />,
            details: [],
            color: "#333",
            showResendButton: true
            });
        } else {
          setError({
            title: "Błąd tworzenia konta",
            message: <span dangerouslySetInnerHTML={{ __html: result.message || "Wystąpił błąd podczas tworzenia konta" }} />,
            details: result.errors || []
          });
        }
        return;
      }
      
      // Success case - show confirmation overlay
      setSuccess({
        title: content?.successOverlay?.title,
        verificationUrl,
        fallbackToEmail
      });
      localStorage.removeItem('autoRegisterFormData');
      trackRegistrationComplete({
        source: "auto_register_form",
        flow: formData.timeTrackingMethod || "unknown",
        registration_method: formData.registrationMethod || null,
        gps_mode: formData.gpsMode || null,
        schedule_usage: formData.scheduleUsage || null
      });
      trackRegistrationEvent("submit_success", {
        source: "auto_register_form",
        flow: formData.timeTrackingMethod || "unknown",
        registration_method: formData.registrationMethod || null,
        gps_mode: formData.gpsMode || null,
        schedule_usage: formData.scheduleUsage || null
      });
      if (SHOW_RESEND_EMAIL_SECTION) {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      }
    } catch (error) {
      console.error("=== Error in user creation process ===");
      console.error("Error details:", error);
      trackRegistrationError({
        source: "auto_register_form",
        flow: formData.timeTrackingMethod || "unknown",
        step: currentStep,
        error_type: "exception",
        message: error?.message || "Unknown error"
      });
      trackRegistrationEvent("submit_exception", {
        source: "auto_register_form",
        flow: formData.timeTrackingMethod || "unknown",
        step: currentStep,
        message: error?.message || "Unknown error"
      });
      // Show server-provided message if available (API always returns a `message` key)
      setError({
        title: "Błąd tworzenia konta",
        message: error?.message || "Wystąpił błąd",
        details: []
      });
    } finally {
      setIsLoading(false); // Stop loading regardless of success or error
    }
  };

  const handleResend = async () => {
    if (!SHOW_RESEND_EMAIL_SECTION) return;
    if (resendDisabled || resendCooldown > 0 || isResending) return;
    try {
      setIsResending(true);
      setResendDisabled(true); // block permanently after click
      // Do not show global loading overlay on resend
      const result = await sendVerificationEmail(formData, true);

      const { shouldRedirectToSms, smsVerificationUrl } = getVerificationRouting(result);
      if (shouldRedirectToSms) {
        await trackRegistrationEvent("sms_redirect_resend", {
          source: "auto_register_form",
          step: currentStep
        });
        localStorage.removeItem('autoRegisterFormData');
        window.location.assign(smsVerificationUrl);
        return;
      }

      // On success, show success overlay
      setSuccess({
        title: content?.successOverlay?.title,
      });
      setError(null); // clear error
      if (SHOW_RESEND_EMAIL_SECTION) {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      }
    } catch (err) {
      setError({ title: "Błąd", message: err?.message || "Nie udało się wysłać wiadomości ponownie", details: [] });
    } finally {
      setIsResending(false);
    }
  };

  const handleBack = () => {
    const prevStep = Math.max(currentStep - 1, 1);
    navigate('/register/' + prevStep);
  };

  const handleErrorBack = () => {
    setError(null);
  };
  
  const handleStepValidationChange = useCallback((step, isValid) => {
    setStepValidation(prev => ({
      ...prev,
      [step]: isValid
    }));
  }, []);

  const isFormValid = () => {
    // Use the comprehensive validation system for all steps
    return stepValidation[currentStep] === true;
  };

  const getStepSnapshot = (step, data) => {
    switch (step) {
      case 1:
        return {
          name: data.name,
          surname: data.surname,
          email: data.email
        };
      case 2:
        return {
          companyName: data.companyName,
          companyAddress: data.companyAddress,
          nip: data.nip,
          acceptedTerms: data.acceptedTerms
        };
      case 3:
        return { companyType: data.companyType };
      case 4:
        return { timeTrackingMethod: data.timeTrackingMethod };
      case 5:
        return {
          gpsMode: data.gpsMode,
          registrationMethod: data.registrationMethod
        };
      case 6:
        return { scheduleUsage: data.scheduleUsage };
      default:
        return {};
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    const finalStep = getMaxStep();

    trackRegistrationEvent("step_snapshot", {
      source: "auto_register_form",
      flow: formData.timeTrackingMethod || "unknown",
      step: currentStep,
      data: getStepSnapshot(currentStep, formData)
    });

    if (currentStep === finalStep) {
      trackRegistrationEvent("step_submit_attempt", {
        source: "auto_register_form",
        flow: formData.timeTrackingMethod || "unknown",
        step: currentStep,
        is_final: true
      });
      setPhoneConfirmationError(null);
      setShowPhoneConfirmationModal(true);
      trackRegistrationEvent("phone_modal_shown", {
        source: "auto_register_form",
        step: currentStep
      });
      return;
    }

    trackRegistrationEvent("step_submit_attempt", {
      source: "auto_register_form",
      flow: formData.timeTrackingMethod || "unknown",
      step: currentStep,
      is_final: false
    });
    handleNextStep();
  };

  const handlePhoneConfirmation = () => {
    const phoneValidation = validatePhoneNumber(formData.phone || "");
    if (!phoneValidation.isValid) {
      setPhoneConfirmationError(phoneValidation.error);
      trackRegistrationEvent("phone_validation_failed", {
        source: "auto_register_form",
        step: currentStep
      });
      return;
    }

    trackRegistrationField("phone", formData.phone, {
      source: "auto_register_form",
      step: currentStep
    });
    trackRegistrationEvent("phone_confirmed", {
      source: "auto_register_form",
      step: currentStep
    });

    setPhoneConfirmationError(null);
    setShowPhoneConfirmationModal(false);

    if (!RECAPTCHA_SITE_KEY) {
      setError({
        title: "Brak konfiguracji reCAPTCHA",
        message: "Formularz nie może zostać wysłany, ponieważ brakuje klucza reCAPTCHA. Skontaktuj się z administratorem.",
        details: []
      });
      return;
    }

    setCaptchaError(null);
    setShowCaptchaModal(true);
    trackRegistrationEvent("captcha_shown", {
      source: "auto_register_form",
      step: currentStep
    });
  };

  const handlePhoneInputChange = (value) => {
    const normalized = normalizePhoneValue(value);
    if (normalized === null) return;

    setFormData((prev) => ({
      ...prev,
      phone: normalized
    }));

    if (phoneConfirmationError) {
      setPhoneConfirmationError(null);
    }
  };

  const handlePhoneModalClose = () => {
    trackRegistrationEvent("phone_modal_cancelled", {
      source: "auto_register_form",
      step: currentStep
    });
    setPhoneConfirmationError(null);
    setShowPhoneConfirmationModal(false);
  };

  // Track the start of the registration flow once
  useEffect(() => {
    if (hasTrackedStart) return;
    trackRegistrationStart({ source: "auto_register_form" });
    trackRegistrationStep(`step_${currentStep}`, {
      source: "auto_register_form",
      flow: formDataRef.current.timeTrackingMethod || "unknown"
    });
    trackRegistrationEvent("step_view", {
      source: "auto_register_form",
      flow: formDataRef.current.timeTrackingMethod || "unknown",
      step: currentStep
    });
    setHasTrackedStart(true);
    setLastTrackedStep(currentStep);
  }, [hasTrackedStart, currentStep]);

  // Track each step transition
  useEffect(() => {
    if (!hasTrackedStart) return;
    if (lastTrackedStep === currentStep) return;

    trackRegistrationStep(`step_${currentStep}`, {
      source: "auto_register_form",
      flow: formDataRef.current.timeTrackingMethod || "unknown"
    });
    trackRegistrationEvent("step_view", {
      source: "auto_register_form",
      flow: formDataRef.current.timeTrackingMethod || "unknown",
      step: currentStep
    });
    setLastTrackedStep(currentStep);
  }, [currentStep, hasTrackedStart, lastTrackedStep]);

  // Auto-select GPS mode when GPS flow is chosen (step for GPS mode is removed)
  useEffect(() => {
    if (formData.timeTrackingMethod !== "gps") return;
    if (formData.gpsMode) return;

    const gpsMethods = content?.stepFive?.gpsMethods;
    if (gpsMethods && typeof gpsMethods === "object") {
      const methodsArray = Object.values(gpsMethods).filter(
        (method) => method && typeof method === "object" && method.id
      );
      const preferred = methodsArray.find((m) => m.recommended && !m.soon);
      const fallbackNonSoon = methodsArray.find((m) => !m.soon);
      const fallbackAny = methodsArray[0];
      const chosen = (preferred || fallbackNonSoon || fallbackAny)?.id || "gps_manual";

      setFormData((prev) => ({
        ...prev,
        gpsMode: chosen
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        gpsMode: "gps_manual"
      }));
    }
  }, [content, formData.timeTrackingMethod, formData.gpsMode, setFormData]);

  const handleCaptchaResolved = async (token) => {
    if (!token) {
      setCaptchaError("Potwierdź, że nie jesteś robotem.");
      return;
    }

    setCaptchaError(null);
    setShowCaptchaModal(false);
    setFormData(prev => ({
      ...prev,
      captchaToken: token
    }));
    trackRegistrationEvent("captcha_resolved", {
      source: "auto_register_form",
      flow: formData.timeTrackingMethod || "unknown",
      step: currentStep
    });

    await handleNewUserCreation({ ...formData, captchaToken: token });
  };

  const handleCaptchaExpired = () => {
    trackRegistrationEvent("captcha_expired", {
      source: "auto_register_form",
      flow: formData.timeTrackingMethod || "unknown",
      step: currentStep
    });
    setCaptchaError("Sesja reCAPTCHA wygasła. Spróbuj ponownie.");
  };

  const handleCaptchaClose = () => {
    trackRegistrationEvent("captcha_closed", {
      source: "auto_register_form",
      flow: formData.timeTrackingMethod || "unknown",
      step: currentStep
    });
    setShowCaptchaModal(false);
  };

  useEffect(() => {
    const fields = [
      "companyType",
      "timeTrackingMethod",
      "gpsMode",
      "registrationMethod",
      "scheduleUsage",
      "acceptedTerms"
    ];

    fields.forEach((field) => {
      const previousValue = selectionRef.current[field];
      const currentValue = formData[field];
      if (previousValue !== currentValue) {
        trackRegistrationSelection(field, currentValue, {
          source: "auto_register_form",
          flow: formData.timeTrackingMethod || "unknown",
          step: currentStep
        });
      }
    });

    selectionRef.current = {
      companyType: formData.companyType,
      timeTrackingMethod: formData.timeTrackingMethod,
      gpsMode: formData.gpsMode,
      registrationMethod: formData.registrationMethod,
      scheduleUsage: formData.scheduleUsage,
      acceptedTerms: formData.acceptedTerms
    };
  }, [
    formData.companyType,
    formData.timeTrackingMethod,
    formData.gpsMode,
    formData.registrationMethod,
    formData.scheduleUsage,
    formData.acceptedTerms,
    currentStep
  ]);

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // Check if all previous steps are valid, if not, navigate to first step
  useEffect(() => {
    if (currentStep <= 1) return;

    for (let step = 1; step < currentStep; step++) {
      if (stepValidation[step] !== true) {
        navigate('/register/1', { replace: true });
        break;
      }
    }
  }, [currentStep, stepValidation, navigate]);

  if (!content) {
    // loading spinner animation
    return (
      <div style={{ backgroundColor: "rgb(68, 68, 68)", height: "100vh", width: "100vw", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center"
        }}>
          <div style={{
            border: "6px solid #f3f3f3",
            borderTop: "6px solid #3498db",
            borderRadius: "50%",
            width: "60px",
            height: "60px",
            animation: "spin 1s linear infinite"
          }} />
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    );  
  }

  return (
    <>
      <PageMeta path="/register" />
      <Helmet>
        <meta name="theme-color" content="#ffffff" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      </Helmet>
      <style>
        {`
          /* Disable text selection for AutoRegisterForm, but allow it for input fields */
          .auto-register-form * {
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
          }

          .auto-register-form input,
          .auto-register-form textarea {
            user-select: text !important;
            -webkit-user-select: text !important;
            -moz-user-select: text !important;
            -ms-user-select: text !important;
          }

          /* Responsive styles for mobile devices */
          @media (max-width: ${MOBILE_VIEW_WIDTH_THRESHOLD}px) {
            .auto-register-form {
              min-height: 100vh;
              padding: 0;
              width: 100vw;
              align-items: center !important;
              justify-content: center !important;
              display: flex !important;
            }
            .auto-register-form > div {
              width: 100vw !important;
              min-width: 0 !important;
              height: auto !important;
              min-height: 100vh;
              flex-direction: column !important;
              border-radius: 0 !important;
              box-shadow: none !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
            .auto-register-form .left-panel,
            .auto-register-form .right-panel {
              width: 100% !important;
              min-width: 0 !important;
              max-width: 100vw !important;
              border-radius: 0 !important;
              padding: 24px 8vw 16px 8vw !important;
              box-sizing: border-box;
            }
            .auto-register-form .left-panel {
              border-bottom: 1px solid #e9ecef;
              border-right: none !important;
              padding-bottom: 12px !important;
            }
            .auto-register-form .right-panel {
              padding-top: 12px !important;
            }
            .auto-register-form .errorContent,
            .auto-register-form .successContent {
              max-width: 95vw !important;
              padding: 24px 8vw !important;
            }
            .auto-register-form .captchaModal {
              min-width: 0 !important;
              width: 95vw !important;
              padding: 18px 4vw !important;
            }
            /* Powiększ inputy na całą szerokość na mobile */
            .auto-register-form input:not([type="checkbox"]),
            .auto-register-form select {
              width: 100% !important;
              min-width: 0 !important;
              box-sizing: border-box !important;
              font-size: 18px !important;
              margin-left: auto !important;
              margin-right: auto !important;
              display: block !important;
            }
            .auto-register-form form {
              width: 100% !important;
              min-width: 0 !important;
              box-sizing: border-box !important;
            }
            .auto-register-form .bottomSection,
            .auto-register-form .bottomSection {
              margin-top: 40px !important;
            }
          }

          @media (max-width: 600px) {
            .auto-register-form > div {
              padding: 0 !important;
            }
            .auto-register-form .left-panel,
            .auto-register-form .right-panel {
              padding: 18px 2vw 12px 2vw !important;
            }
            .auto-register-form .errorContent,
            .auto-register-form .successContent {
              padding: 18px 2vw !important;
            }
            .auto-register-form .captchaModal {
              width: 98vw !important;
              padding: 12px 2vw !important;
            }
            .auto-register-form input:not([type="checkbox"]),
            .auto-register-form textarea,
            .auto-register-form select {
              font-size: 17px !important;
              max-width: 98vw !important;
            }
            .auto-register-form .bottomSection,
            .auto-register-form .bottomSection {
              margin-top: 32px !important;
            }
          }
        `}
      </style>
      <div className="auto-register-form" style={styles.page}>
        <div style={styles.container}>
          <LeftPanel 
            step={currentStep} 
            isCompact={currentStep > 2} 
            content={content.leftPanel} 
            timeTrackingMethod={formData.timeTrackingMethod}
            isMobileView={IS_MOBILE_VIEW}
            className="left-panel"
          />
          <RightPanel 
            step={currentStep}
            formData={formData}
            setFormData={setFormData}
            isFormValid={isFormValid}
            handleSubmit={handleSubmit}
            currentStep={currentStep}
            onBack={handleBack}
            isExpanded={currentStep > 2}
            content={content.rightPanel}
            fullContent={content}
            onStepValidationChange={handleStepValidationChange}
            hideRegistrationMethodStep={HIDE_REGISTRATION_METHOD_STEP}
            className="right-panel"
            isMobileView={IS_MOBILE_VIEW}
          />
        </div>
      
      {showCaptchaModal && (
        <div style={styles.captchaOverlay}>
          <div style={{...styles.captchaModal}} className="captchaModal">
            <h3 style={styles.captchaTitle}>Potwierdź, że nie jesteś robotem</h3>
            <ReCAPTCHA
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={handleCaptchaResolved}
              onExpired={handleCaptchaExpired}
            />
            {captchaError && <div style={styles.captchaError}>{captchaError}</div>}
            <button style={styles.captchaCloseButton} onClick={handleCaptchaClose}>Anuluj</button>
          </div>
        </div>
      )}

      {showPhoneConfirmationModal && (
        <div style={styles.captchaOverlay}>
          <div style={{...styles.captchaModal}} className="captchaModal">
            <h3 style={styles.captchaTitle}>Zweryfikuj kod SMS</h3>
            <p style={styles.phoneConfirmationText}>
              Wpisz numer telefonu, na który mamy wysłać kod SMS:
            </p>
            <input
              type="text"
              placeholder="+48 123 456 789"
              value={formData.phone || ""}
              onChange={(e) => handlePhoneInputChange(e.target.value)}
              style={styles.phoneConfirmationInput}
            />
            {phoneConfirmationError && (
              <div style={styles.phoneConfirmationError}>{phoneConfirmationError}</div>
            )}
            <div style={styles.phoneConfirmationButtons}>
              <button style={styles.phoneConfirmButton} onClick={handlePhoneConfirmation}>
                Wyślij kod SMS
              </button>
              <button style={styles.phoneEditButton} onClick={handlePhoneModalClose}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Spinner Overlay */}
      {isLoading && (
        <div style={styles.loadingOverlay}>
          <div style={styles.loadingContent}>
            <div style={styles.spinner}></div>
          </div>
        </div>
      )}
      
      {/* Error Screen Overlay */}
      {error && (
        <div style={styles.errorOverlay}>
          <div style={styles.errorContent} className="errorContent">
            <h2 style={{ ...styles.errorTitle, color: error.color || "#e74c3c" }}>{error.title}</h2>
            <p style={styles.errorMessage}>{error.message}</p>
            {error.details && error.details.length > 0 && (
              <div style={styles.errorDetails}>
                <h4 style={styles.errorDetailsTitle}>Szczegóły błędu:</h4>
                <ul style={styles.errorDetailsList}>
                  {error.details.map((detail, index) => (
                    <li key={index} style={styles.errorDetailsItem}>{detail}</li>
                  ))}
                </ul>
              </div>
            )}
            <div style={styles.errorButtonContainer}>
              {error.showResendButton ? (
                <button onClick={handleResend} style={{ ...styles.successButton, opacity: resendDisabled || resendCooldown > 0 || isResending ? 0.6 : 1, cursor: resendDisabled || resendCooldown > 0 || isResending ? 'not-allowed' : 'pointer' }} disabled={resendDisabled || resendCooldown > 0 || isResending}>
                  {resendCooldown > 0 ? `${content.successOverlay.wait} ${resendCooldown}${content.successOverlay.secondsSuffix}` : content.successOverlay.resend}
                </button>
              ) : (
                <>
                  <button onClick={handleErrorBack} style={styles.errorButton}>
                    Wróć do formularza
                  </button>
                  <button onClick={() => window.location = '/register/1'} style={styles.retryButton}>
                    Spróbuj jeszcze raz
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success Screen Overlay */}
      {success && (
        <div style={styles.errorOverlay}>
          <div style={styles.errorContent} className="successContent">
            <h2 style={styles.successTitle}>{success.title}</h2>

            {formData?.email && (
              <p style={styles.errorMessage}>{content.successOverlay.emailInfoPrefix} <span style={{ fontWeight: 700 }}>{formData.email}</span>.</p>
            )}
            <p style={styles.errorMessage}>{content.successOverlay.closeInfo}</p>

            {SHOW_RESEND_EMAIL_SECTION && (
              <>
                <div style={{ marginTop: "20px", textAlign: "left", marginLeft: IS_MOBILE_VIEW ? "20px" : "auto", marginRight: IS_MOBILE_VIEW ? "20px" : "auto", maxWidth: IS_MOBILE_VIEW ? "100%" : "none" }}>
                  <h4 style={styles.errorDetailsTitle}>{content.successOverlay.didntReceive}</h4>
                  <p style={styles.errorMessage}>{content.successOverlay.checkSpam}</p>
                </div>

                <div style={styles.errorButtonContainer}>
                  <button onClick={handleResend} style={{ ...styles.successButton, opacity: resendDisabled || resendCooldown > 0 || isResending ? 0.6 : 1, cursor: resendDisabled || resendCooldown > 0 || isResending ? 'not-allowed' : 'pointer' }} disabled={resendDisabled || resendCooldown > 0 || isResending}>
                    {resendCooldown > 0 ? `${content.successOverlay.wait} ${resendCooldown}${content.successOverlay.secondsSuffix}` : content.successOverlay.resend}
                  </button>
                </div>
              </>
            )}

            <div style={styles.errorButtonContainer}></div>
          </div>
        </div>
      )}
      
      {/* CSS Animation for Spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      </div>
    </>
  );
}

const styles = {
  page: {
    font: 'Cabin',
    backgroundColor: "#444444",
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  container: {
    display: "flex",
    position: "relative",
    width: "1050px",
    height: "711px",
    borderRadius: "40px",
    overflow: "hidden",
    backgroundColor: "#fff"
  },
  loadingOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(68, 68, 68, 0.9)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  loadingContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
  },
  spinner: {
    border: "6px solid #f3f3f3",
    borderTop: "6px solid #3498db",
    borderRadius: "50%",
    width: "60px",
    height: "60px",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    color: "#fff",
    fontSize: "18px",
    fontFamily: "Cabin, sans-serif",
    margin: 0,
  },
  errorOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(68, 68, 68, 0.95)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  errorContent: {
    backgroundColor: "#fff",
    borderRadius: "20px",
    padding: "40px",
    maxWidth: "500px",
    width: "90%",
    textAlign: "center",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
  },
  errorIcon: {
    fontSize: "60px",
    marginBottom: "20px",
  },
  errorTitle: {
    color: "#e74c3c",
    fontSize: "32px",
    fontFamily: "Cabin, sans-serif",
    margin: "0 0 15px 0",
    fontWeight: "bold",
  },
  successTitle: {
    color: "#407EF9",
    fontSize: "32px",
    fontFamily: "Cabin, sans-serif",
    margin: "0 0 15px 0",
    fontWeight: "bold",
  },
  errorMessage: {
    color: "#333",
    fontSize: "16px",
    fontFamily: "Cabin, sans-serif",
    margin: "0 0 20px 0",
    lineHeight: "1.5",
  },
  errorDetails: {
    textAlign: "left",
    margin: "20px 0",
    padding: "15px",
    backgroundColor: "#f8f9fa",
    borderRadius: "10px",
    border: "1px solid #e9ecef",
  },
  errorDetailsTitle: {
    color: "#495057",
    fontSize: "14px",
    fontFamily: "Cabin, sans-serif",
    margin: "0 0 10px 0",
    fontWeight: "bold",
  },
  errorDetailsList: {
    color: "#6c757d",
    fontSize: "14px",
    fontFamily: "Cabin, sans-serif",
    margin: "0",
    paddingLeft: "20px",
  },
  errorDetailsItem: {
    margin: "5px 0",
    lineHeight: "1.4",
  },
  errorButton: {
    backgroundColor: "#407EF9",
    color: "#fff",
    border: "none",
    borderRadius: "25px",
    padding: "10px 20px",
    fontSize: "16px",
    fontFamily: "Cabin, sans-serif",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
  },
  successButton: {
    backgroundColor: "#407EF9",
    color: "#fff",
    border: "none",
    borderRadius: "25px",
    padding: "10px 20px",
    fontSize: "16px",
    fontFamily: "Cabin, sans-serif",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
  },
  errorButtonContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "15px",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "20px",
  },
  retryButton: {
    backgroundColor: "rgb(131, 131, 131)",
    color: "#fff",
    border: "none",
    borderRadius: "25px",
    padding: "10px 20px",
    fontSize: "16px",
    fontFamily: "Cabin, sans-serif",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
  },
  captchaOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000
  },
  captchaModal: {
    backgroundColor: "#fff",
    padding: "30px",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "15px",
    minWidth: "320px"
  },
  captchaTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
    textAlign: "center"
  },
  captchaError: {
    color: "#e74c3c",
    fontSize: "14px",
    textAlign: "center"
  },
  phoneConfirmationText: {
    margin: 0,
    color: "#333",
    fontSize: "15px",
    textAlign: "center",
    lineHeight: "1.5"
  },
  phoneConfirmationInput: {
    width: "100%",
    maxWidth: "320px",
    height: "44px",
    border: "1px solid #CCDCFC",
    borderRadius: "12px",
    fontSize: "16px",
    color: "#333",
    padding: "0 12px",
    fontFamily: "Cabin, sans-serif",
    outline: "none",
    boxSizing: "border-box"
  },
  phoneConfirmationError: {
    marginTop: "-8px",
    color: "#e74c3c",
    fontSize: "13px",
    textAlign: "center"
  },
  phoneConfirmationButtons: {
    display: "flex",
    gap: "10px",
    marginTop: "5px",
    flexWrap: "wrap",
    justifyContent: "center"
  },
  phoneConfirmButton: {
    backgroundColor: "#407EF9",
    color: "#fff",
    border: "none",
    borderRadius: "20px",
    padding: "10px 16px",
    cursor: "pointer",
    fontFamily: "Cabin, sans-serif"
  },
  phoneEditButton: {
    backgroundColor: "#6c757d",
    color: "#fff",
    border: "none",
    borderRadius: "20px",
    padding: "10px 16px",
    cursor: "pointer",
    fontFamily: "Cabin, sans-serif"
  },
  captchaCloseButton: {
    marginTop: "5px",
    backgroundColor: "#4d8bee",
    color: "#fff",
    border: "none",
    borderRadius: "20px",
    padding: "8px 16px",
    cursor: "pointer"
  },
};

export default AutoRegisterForm;
